// CoursesApp.tsx — a BOARD APP: every course worth taking, in one place.
//
// Fifth of the "Board App" pattern and a deliberate sibling of OpenSourceApp:
// same 1700x1180 frame, same one-card-with-a-divider chrome, same
// sidebar / gallery / detail shape. If they look like two different products
// sitting next to each other on the board, that is a bug.
//
//   LEFT   — search, STAGE and category filters, the full list.
//   RIGHT  — HOME is a gallery of every course; picking one swaps the pane for
//            the DETAIL page, which renders our own write-up: what it actually
//            teaches, what layer it sits at, and what we take vs leave.
//
// Data: /api/courses (list) + /api/courses/:slug (with body). The body is
// markdown, rendered through the shared blueprint/mdToBlocks.
//
// WHY STAGE + TAKEAWAY ARE THE POINT: a list of course links is a bookmark
// folder. What makes this ours is that every row carries whether we ACTUALLY
// took it, and the one line we got out of it. The skipped rows matter most —
// they stop us re-opening the same tab in three months.
//
// React-Flow traps, same as its siblings: pointerEvents auto plus
// nowheel/nodrag/nopan on everything interactive or scrolling, and useApi() is
// deliberately kept out of the effect deps so it does not refetch forever.

import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, GraduationCap, ChevronLeft, ExternalLink } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";
import { BlockRenderer } from "../blueprint/BlockRenderer";
import { bodyToBlocks } from "../blueprint/mdToBlocks";

const T = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  line: "#ECE7DF", hair: "#E3DED4",
  green: "#3F8F6F", greenDeep: "#2E6B52", greenWash: "#EEF5F1",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Matches AgentBrainApp + SkillsApp + OpenSourceApp exactly.
const LIST_W = 300;
const MAIN_W = 1400;
const H = 1180;

type Stage = "to-take" | "taking" | "taken" | "skipped";

type Course = {
  id: string;
  slug: string;
  title: string;
  provider: string | null;
  tagline: string | null;
  url: string | null;
  source: string | null;
  cost: string | null;
  duration: string | null;
  level: string | null;
  format: string | null;
  category: string;
  topics: string[];
  stage: Stage;
  takeaway: string | null;
  body_chars: number;
  noticed_at: string;
  updated_at: string;
};
type Detail = Omit<Course, "body_chars"> & { body: string | null };

// The stage is the spine of the directory, so it gets the colour.
const STAGE: Record<Stage, { label: string; colour: string; wash: string; note: string }> = {
  "taken":   { label: "taken",   colour: "#2E6B52", wash: "#EEF5F1", note: "we went through it — takeaway recorded" },
  "taking":  { label: "taking",  colour: "#2F6BB0", wash: "#EDF3FA", note: "in progress right now" },
  "to-take": { label: "to take", colour: "#B07A1E", wash: "#FBF4E6", note: "queued — worth the hours" },
  "skipped": { label: "skipped", colour: "#B0432E", wash: "#FBEEEB", note: "decided against, reason recorded" },
};
const STAGE_ORDER: Stage[] = ["taken", "taking", "to-take", "skipped"];

const chip = (on: boolean): React.CSSProperties => ({
  fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
  textTransform: "uppercase", cursor: "pointer", pointerEvents: "auto",
  color: on ? T.greenDeep : T.faint,
  background: on ? T.greenWash : "transparent",
  border: `1px solid ${on ? T.green : T.hair}`,
  borderRadius: 8, padding: "4px 10px",
});

export function CoursesApp() {
  const api = useApi();
  const [items, setItems] = useState<Course[] | "loading">("loading");
  const [slug, setSlug] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | "loading" | null>(null);

  const [q, setQ] = useState("");
  const [stage, setStage] = useState<"all" | Stage>("all");
  const [cat, setCat] = useState<string>("all");

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await api("/api/courses");
        const rows = (await r.json()) as Course[];
        if (!dead) setItems(rows);
      } catch { if (!dead) setItems([]); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!slug) { setDetail(null); return; }
    let dead = false;
    setDetail("loading");
    (async () => {
      try {
        const r = await api(`/api/courses/${slug}`);
        const row = (await r.json()) as Detail;
        if (!dead) setDetail(row);
      } catch { if (!dead) setDetail(null); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const shown = useMemo(() => {
    if (items === "loading") return [];
    const needle = q.trim().toLowerCase();
    return items.filter(c => {
      if (stage !== "all" && c.stage !== stage) return false;
      if (cat !== "all" && c.category !== cat) return false;
      if (!needle) return true;
      return (
        c.title.toLowerCase().includes(needle) ||
        (c.tagline || "").toLowerCase().includes(needle) ||
        (c.provider || "").toLowerCase().includes(needle) ||
        c.topics.some(t => t.toLowerCase().includes(needle))
      );
    });
  }, [items, q, stage, cat]);

  const cats = useMemo(() => {
    if (items === "loading") return [];
    return [...new Set(items.map(c => c.category))].sort();
  }, [items]);

  const counts = useMemo(() => {
    if (items === "loading") return {} as Record<string, number>;
    return items.reduce<Record<string, number>>((a, c) => {
      a[c.stage] = (a[c.stage] ?? 0) + 1; return a;
    }, {});
  }, [items]);

  const loading = (
    <div style={{ padding: 26, display: "inline-flex", alignItems: "center", gap: 8, fontFamily: FONT, fontSize: 13, color: T.faint }}>
      <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading…
    </div>
  );

  const meta = (c: { cost: string | null; duration: string | null; format: string | null; level: string | null }) => {
    const bits = [c.cost, c.duration, c.format, c.level].filter(Boolean);
    return bits.length ? bits.join("  ·  ") : "details not recorded";
  };

  return (
    <div
      className="nopan nodrag"
      style={{ display: "flex", width: LIST_W + MAIN_W, height: H, fontFamily: FONT, pointerEvents: "auto", background: T.card, border: `1px solid ${T.hair}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 24px rgba(25,29,27,0.05)" }}
    >
      {/* ── SIDEBAR ─────────────────────────────────────────── */}
      <aside
        className="nowheel nodrag nopan"
        style={{ width: LIST_W, flexShrink: 0, overflow: "auto", background: T.paper, borderRight: `1px solid ${T.line}`, padding: "22px 18px 26px", pointerEvents: "auto", boxSizing: "border-box" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <GraduationCap style={{ width: 15, height: 15, color: T.green }} />
          <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.greenDeep }}>
            Courses
          </span>
        </div>
        <div style={{ fontSize: 12, color: T.faint, marginBottom: 16 }}>
          {items === "loading" ? "…" : `${shown.length} of ${items.length} · what we learn from, and what we skip`}
        </div>

        <div style={{ position: "relative", marginBottom: 14 }}>
          <Search style={{ width: 13, height: 13, color: T.faint, position: "absolute", left: 11, top: 10 }} />
          <input
            className="nodrag nopan" value={q} onChange={e => setQ(e.target.value)}
            onPointerDown={e => e.stopPropagation()} placeholder="Search courses…"
            style={{ fontFamily: FONT, fontSize: 13, color: T.ink, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 9, padding: "8px 12px 8px 32px", width: "100%", boxSizing: "border-box", outline: "none", pointerEvents: "auto" }}
          />
        </div>

        {/* STAGE — the spine */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
          <button className="nodrag nopan" style={chip(stage === "all")}
            onPointerDown={e => e.stopPropagation()} onClick={() => setStage("all")}>
            all stages
          </button>
          {STAGE_ORDER.filter(s => counts[s]).map(s => {
            const on = stage === s, m = STAGE[s];
            return (
              <button key={s} className="nodrag nopan"
                onPointerDown={e => e.stopPropagation()} onClick={() => setStage(s)}
                style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", fontFamily: FONT, fontSize: 12, fontWeight: 600, color: on ? T.card : m.colour, background: on ? m.colour : m.wash, border: `1px solid ${m.colour}`, borderRadius: 9, padding: "6px 9px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <span>{m.label}</span>
                <span style={{ fontFamily: MONO, fontSize: 10, opacity: 0.85 }}>{counts[s]}</span>
              </button>
            );
          })}
        </div>

        {/* category */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          <button className="nodrag nopan" style={chip(cat === "all")}
            onPointerDown={e => e.stopPropagation()} onClick={() => setCat("all")}>
            all areas
          </button>
          {cats.map(c => (
            <button key={c} className="nodrag nopan" style={chip(cat === c)}
              onPointerDown={e => e.stopPropagation()} onClick={() => setCat(c)}>
              {c}
            </button>
          ))}
        </div>

        {slug && (
          <button className="nodrag nopan" onPointerDown={e => e.stopPropagation()} onClick={() => setSlug(null)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: T.greenDeep, background: "transparent", border: "none", cursor: "pointer", pointerEvents: "auto", padding: 0, marginBottom: 14 }}>
            <ChevronLeft style={{ width: 13, height: 13 }} /> All courses
          </button>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {shown.map(c => {
            const on = c.slug === slug;
            return (
              <button key={c.id} className="nodrag nopan"
                onPointerDown={e => e.stopPropagation()} onClick={() => setSlug(c.slug)}
                style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", background: on ? T.card : "transparent", border: `1px solid ${on ? T.hair : "transparent"}`, borderLeft: `2px solid ${on ? STAGE[c.stage].colour : "transparent"}`, borderRadius: 8, padding: "8px 10px", fontFamily: FONT }}>
                <div style={{ fontSize: 13, fontWeight: on ? 600 : 500, color: T.ink, lineHeight: 1.3 }}>{c.title}</div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: STAGE[c.stage].colour, marginTop: 2 }}>
                  {STAGE[c.stage].label}{c.provider ? ` · ${c.provider}` : ""}
                </div>
              </button>
            );
          })}
          {items !== "loading" && shown.length === 0 && (
            <div style={{ fontSize: 12, color: T.faint, padding: "10px 2px" }}>Nothing matches.</div>
          )}
        </div>
      </aside>

      {/* ── MAIN ────────────────────────────────────────────── */}
      <main
        className="nowheel nodrag nopan"
        style={{ flex: 1, minWidth: 0, overflow: "auto", background: T.card, pointerEvents: "auto", boxSizing: "border-box" }}
      >
        {items === "loading" ? loading : slug ? (
          detail === "loading" ? loading : !detail ? (
            <div style={{ padding: 26, fontSize: 13, color: T.faint }}>Could not load that course.</div>
          ) : (
            <div style={{ padding: "34px 40px 56px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.card, background: STAGE[detail.stage].colour, borderRadius: 6, padding: "3px 8px" }}>
                  {STAGE[detail.stage].label}
                </span>
                <span style={{ fontSize: 12, color: T.faint }}>{STAGE[detail.stage].note}</span>
              </div>
              <h2 style={{ fontSize: 30, fontWeight: 700, color: T.ink, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                {detail.title}
              </h2>
              {detail.tagline && (
                <p style={{ fontSize: 15.5, color: T.ink2, lineHeight: 1.6, margin: "0 0 12px", maxWidth: "72ch" }}>
                  {detail.tagline}
                </p>
              )}
              <div style={{ fontFamily: MONO, fontSize: 11, color: T.faint, marginBottom: 14 }}>
                {[detail.provider, detail.category].filter(Boolean).join("  ·  ")}  ·  {meta(detail)}
              </div>

              {detail.takeaway && (
                <div style={{ background: T.greenWash, border: `1px solid ${T.green}`, borderRadius: 12, padding: "13px 16px", marginBottom: 20, maxWidth: "80ch" }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.greenDeep, marginBottom: 5 }}>
                    The takeaway
                  </div>
                  <div style={{ fontSize: 14, color: T.ink, lineHeight: 1.55 }}>{detail.takeaway}</div>
                </div>
              )}

              <div style={{ display: "flex", gap: 14, marginBottom: 24 }}>
                {detail.url && (
                  <a href={detail.url} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: T.greenDeep, textDecoration: "none", pointerEvents: "auto" }}>
                    <ExternalLink style={{ width: 12, height: 12 }} /> Open the course
                  </a>
                )}
              </div>
              {detail.source && (
                <div style={{ fontFamily: MONO, fontSize: 10.5, color: T.faint, marginBottom: 20 }}>
                  Found via {detail.source}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 24 }}>
                {(() => {
                  const blocks = bodyToBlocks(detail.body);
                  return blocks && blocks.length
                    ? <BlockRenderer config={architectureConfig} blocks={blocks} />
                    : <div style={{ fontSize: 13, color: T.faint }}>No write-up yet.</div>;
                })()}
              </div>
            </div>
          )
        ) : (
          <div style={{ padding: "30px 32px 40px" }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: T.ink, margin: "0 0 4px", letterSpacing: "-0.01em" }}>
              Courses worth the hours
            </h2>
            <p style={{ fontSize: 13, color: T.ink2, margin: "0 0 24px", maxWidth: "78ch" }}>
              Every course carries a stage and a takeaway, not just a link — did we actually take it, and what
              did we get. The skipped ones matter most: they stop us re-opening the same tab in three months.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(262px, 1fr))", gap: 14 }}>
              {shown.map(c => {
                const s = STAGE[c.stage];
                return (
                  <button key={c.id} className="nodrag nopan"
                    onPointerDown={e => e.stopPropagation()} onClick={() => setSlug(c.slug)}
                    style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", background: T.paper, border: `1px solid ${T.hair}`, borderTop: `3px solid ${s.colour}`, borderRadius: 14, padding: "14px 16px", fontFamily: FONT, display: "flex", flexDirection: "column", gap: 7, minHeight: 158 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: s.colour, background: s.wash, border: `1px solid ${s.colour}`, borderRadius: 5, padding: "2px 6px" }}>
                        {s.label}
                      </span>
                      {c.cost && (
                        <span style={{ fontFamily: MONO, fontSize: 10, color: T.faint }}>{c.cost}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 14.5, fontWeight: 600, color: T.ink, lineHeight: 1.28 }}>{c.title}</div>
                    <div style={{ fontSize: 12, color: T.ink2, lineHeight: 1.5, flex: 1 }}>
                      {(c.tagline || "").slice(0, 118)}{(c.tagline || "").length > 118 ? "…" : ""}
                    </div>
                    {c.topics.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {c.topics.slice(0, 4).map(t => (
                          <span key={t} style={{ fontFamily: MONO, fontSize: 9.5, color: T.ink2, background: T.bone, border: `1px solid ${T.hair}`, borderRadius: 5, padding: "1.5px 5px" }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ fontFamily: MONO, fontSize: 10, color: T.faint, borderTop: `1px solid ${T.line}`, paddingTop: 7 }}>
                      {[c.provider, c.duration].filter(Boolean).join("  ·  ") || "details not recorded"}
                    </div>
                  </button>
                );
              })}
            </div>
            {shown.length === 0 && (
              <div style={{ fontSize: 13, color: T.faint, paddingTop: 12 }}>Nothing matches those filters.</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default CoursesApp;
